import {
  AutocompleteResult,
  autocompleteResultsByType,
  AutocompleteType,
  Contact,
  FromToType,
  GET_AUTO_COMPLETE,
  Task,
  TaskDirection,
  TaskType,
} from '@admiin-com/ds-graphql';
import { gql, useLazyQuery } from '@apollo/client';
import React from 'react';
import { getName } from '../../helpers/contacts';
import { AnnotationDocument, getTaskDirection } from '../../helpers/tasks';
import { useCurrentEntityId } from '../useSelectedEntity/useSelectedEntity';

export const useTaskToContactName = (task: Task | null) => {
  const [contactLoading, setContactLoading] = React.useState(false);
  const [contactName, setContactName] = React.useState<string | null>(null);
  const [futureLinkedContact, setFutureLinkedContact] =
    React.useState<Contact | null>(null);
  const [data, setData] = React.useState<Contact | null>(null);
  const getTaskToName = useTaskToName();
  const entityId = useCurrentEntityId();

  React.useEffect(() => {
    if (
      task?.type === TaskType.PAY_ONLY &&
      task?.fromType === FromToType.ENTITY &&
      task?.toType === FromToType.ENTITY
    ) {
      setContactLoading(true);
      getTaskToName(task.futureLinkedTask?.fromId ?? task.fromId).then(
        (data) => {
          setContactName(data.name ?? '');
          setContactLoading(false);
          setData(data.data);
        }
      );
    } else if (task?.contactId) {
      setContactLoading(true);
      getTaskToName(task?.contactId).then((data) => {
        setContactName(data.name ?? '');
        setContactLoading(false);
        setData(data.data);
      });
      if (task.type === 'SIGN_PAY' && task.futureLinkedTask) {
        if (task.direction === 'RECEIVING') {
          getTaskToName(task.futureLinkedTask.fromId).then(({ data }) =>
            setFutureLinkedContact(data)
          );
        } else {
          getTaskToName(task.futureLinkedTask.toId).then(({ data }) =>
            setFutureLinkedContact(data)
          );
        }
      } else {
        setFutureLinkedContact(null);
      }
    } else if (
      task?.type === TaskType.SIGN_ONLY ||
      task?.type === TaskType.SIGN_PAY
    ) {
      const annotations = task?.annotations;
      if (typeof annotations === 'string') {
        try {
          const annotationsObj = JSON.parse(annotations) as AnnotationDocument;
          const names: string[] = [];
          const parsedAnnotations = annotationsObj.annotations;

          if (task?.direction === TaskDirection.SENDING) {
            for (const annotation of parsedAnnotations) {
              if (
                (annotation?.customData?.signerType === 'CONTACT' ||
                  annotation?.customData?.signerType === 'GUEST') &&
                annotation?.customData.type === 'SIGNATURE' &&
                annotation?.customData.name &&
                !names.includes(annotation?.customData?.name as string)
              ) {
                names.push(annotation?.customData?.name as string);
              }
            }

            setContactName(names.join(', '));
          }

          // RECEIVING
          else {
            if (task?.fromId) {
              getTaskToName(task.fromId).then((data) => {
                setContactName(data.name ?? '');
                setContactLoading(false);
                setData(data.data);
              });
            } else {
              setContactLoading(false);
            }
          }
        } catch (err) {
          console.error('Error parsing annotations:', err);
        }
      }
      setContactLoading(false);
    } else {
      setContactName(null);
      setData(null);
      setContactLoading(false);
    }
  }, [entityId, task]);

  return {
    contactName,
    contactLoading,
    data,
    futureLinkedContact,
    name: futureLinkedContact?.name ?? contactName,
  };
};

export const useTaskFromToName = (task: Task | null) => {
  const getTaskToName = useTaskToName();
  const [from, setFrom] = React.useState<string | null>(null);
  const [to, setTo] = React.useState<string | null>(null);

  const [futureInputFrom, setFutureInputFrom] = React.useState<string | null>(
    null
  );
  const [futureInputTo, setFutureInputTo] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (task?.fromId) {
      getTaskToName(task?.fromId).then((data) => {
        setFrom(data.name ?? '');
      });
    }
    if (task?.toId) {
      getTaskToName(task?.toId).then((data) => {
        setTo(data.name ?? '');
      });
    }
    if (task?.futureLinkedTask?.fromId) {
      getTaskToName(task?.futureLinkedTask?.fromId).then((data) => {
        setFutureInputFrom(data.name ?? '');
      });
    }
    if (task?.futureLinkedTask?.toId) {
      getTaskToName(task?.futureLinkedTask?.toId).then((data) => {
        setFutureInputTo(data.name ?? '');
      });
    }
  }, [task]);
  return {
    from,
    to,
    futureInputFrom,
    futureInputTo,
    fromName: futureInputFrom ?? from,
    toName: futureInputTo ?? to,
  };
};

export const useTaskToName = () => {
  const [getAutoComplete] = useLazyQuery(gql(GET_AUTO_COMPLETE), {
    nextFetchPolicy: 'cache-first',
    returnPartialData: true,
  });
  const [getAutoCompletes] = useLazyQuery(gql(autocompleteResultsByType), {
    variables: {
      type: AutocompleteType.ENTITY,
      searchName: '',
    },
    nextFetchPolicy: 'cache-first',
    returnPartialData: true,
  });
  const getTaskToName = React.useCallback(
    async (
      id: string | null | undefined,
      inAutoCompletes: boolean | undefined = false
    ) => {
      if (id) {
        try {
          if (inAutoCompletes) {
            const { data: autoCompleteResultsData } = await getAutoCompletes();
            const autoCompleteResults =
              autoCompleteResultsData?.autocompleteResultsByType?.items ?? [];
            const autoCompleteResult = autoCompleteResults.find(
              (item: AutocompleteResult) => item.id === id
            );
            if (autoCompleteResult) return { data: autoCompleteResult };
          }

          const data = await getAutoComplete({
            variables: { id },
          });
          const contactData = data?.data;
          const contact =
            contactData?.getAutoComplete?.contact ||
            contactData?.getAutoComplete?.entity;
          return { data: contact, name: getName(contact) ?? '' };
        } catch (e: any) {
          console.error('Error reading cache:', e);
          throw new Error(e);
        }
      } else {
        return { name: '', data: null };
      }
    },
    []
  );
  return getTaskToName;
};

export const useTaskSelfName = (task: Task | null) => {
  const [selfName, setSelfName] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const getTaskToName = useTaskToName();
  const [id, setId] = React.useState<string | null | undefined>(null);
  const entityId = useCurrentEntityId();

  React.useEffect(() => {
    if (!task || !entityId) {
      setSelfName(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    // Determine the direction relative to the current entity
    // Get the ID of the entity on "our" side of the task
    const selfId =
      task.direction === TaskDirection.SENDING ? task.fromId : task.toId;
    setId(selfId);

    if (selfId) {
      getTaskToName(selfId)
        .then((data) => {
          setSelfName(data.name ?? '');
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching self name:', err);
          setSelfName(null);
          setLoading(false);
        });
    } else {
      // Handle cases where the selfId might not be directly available
      // or if the task structure doesn't fit this logic perfectly.
      // You might need to add more specific logic based on task types if needed.
      setSelfName(null); // Or potentially fetch entity name via entityId if appropriate
      setLoading(false);
    }
  }, [task, entityId, getTaskToName]);

  return { selfName, loading, id };
};
